import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import axios from 'axios';
import apiConfig from '../apiConfig';

const Checkout = () => {
    const navigate = useNavigate();
    const { cart, getSubtotal, clearCart } = useContext(CartContext);
    const [loading, setLoading] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState('');
    const [checkoutRequestID, setCheckoutRequestID] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        notes: ''
    });

    const deliveryFee = 200;
    const total = getSubtotal() + deliveryFee;

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setPaymentStatus('Initiating payment...');

        try {
            // Prepare order data
            const orderData = {
                email: formData.email,
                name: formData.name,
                phone: formData.phone,
                address: formData.address,
                city: formData.city,
                notes: formData.notes,
                items: cart.map(item => ({
                    productId: item._id,
                    quantity: item.quantity
                }))
            };

            // Create order and initiate M-Pesa payment
            const response = await axios.post(`${apiConfig.baseURL}/orders/checkout`, orderData);

            if (response.data.success) {
                setCheckoutRequestID(response.data.checkoutRequestID);
                setShowPaymentModal(true);
                setPaymentStatus('Check your phone for M-Pesa prompt');

                // Start polling for payment status
                pollPaymentStatus(response.data.checkoutRequestID);
            } else {
                alert('Checkout failed. Please try again.');
                setLoading(false);
            }

        } catch (error) {
            console.error('Checkout error:', error);
            alert('Checkout failed. Please try again.');
            setLoading(false);
        }
    };

    const pollPaymentStatus = async (requestID) => {
        const maxAttempts = 24; // Poll for 2 minutes (24 * 5 seconds)
        let attempts = 0;

        const interval = setInterval(async () => {
            attempts++;

            try {
                const response = await axios.get(`${apiConfig.baseURL}/orders/status/${requestID}`);

                if (response.data.success) {
                    const { status, orderStatus, orderNumber } = response.data;

                    if (status === 'completed' && orderStatus === 'paid') {
                        // Payment successful
                        clearInterval(interval);
                        setPaymentStatus('Payment successful! Redirecting...');

                        // Store order number for confirmation page
                        localStorage.setItem('lastOrderNumber', orderNumber);

                        // Clear cart and redirect
                        setTimeout(() => {
                            clearCart();
                            navigate('/order-confirmation');
                        }, 2000);

                    } else if (status === 'failed') {
                        // Payment failed
                        clearInterval(interval);
                        setPaymentStatus('Payment failed. Please try again.');
                        setLoading(false);

                        setTimeout(() => {
                            setShowPaymentModal(false);
                        }, 3000);

                    } else if (attempts >= maxAttempts) {
                        // Timeout
                        clearInterval(interval);
                        setPaymentStatus('Payment timeout. Please check your M-Pesa messages.');
                        setLoading(false);

                        setTimeout(() => {
                            setShowPaymentModal(false);
                        }, 3000);
                    }
                }

            } catch (error) {
                console.error('Status check error:', error);

                if (attempts >= maxAttempts) {
                    clearInterval(interval);
                    setPaymentStatus('Unable to verify payment. Please contact support.');
                    setLoading(false);
                }
            }

        }, 5000); // Poll every 5 seconds
    };

    if (cart.length === 0) {
        navigate('/cart');
        return null;
    }

    return (
        <div className="container checkout-page">
            <h1>Checkout</h1>

            <div className="checkout-content">
                <div className="checkout-form">
                    <h2>Customer Information</h2>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Full Name *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="form-group">
                                <label>Phone Number (M-Pesa) *</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="254712345678"
                                    required
                                    disabled={loading}
                                />
                                <small>Format: 254XXXXXXXXX</small>
                            </div>
                        </div>

                        <h2>Delivery Information</h2>

                        <div className="form-group">
                            <label>Delivery Address *</label>
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Street address, building, apartment number"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label>City *</label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label>Order Notes (Optional)</label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Any special instructions for delivery"
                                disabled={loading}
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-checkout"
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : `Pay KSh ${total.toFixed(2)} with M-Pesa`}
                        </button>
                    </form>
                </div>

                <div className="checkout-summary">
                    <h2>Order Summary</h2>

                    <div className="summary-items">
                        {cart.map(item => (
                            <div key={item._id} className="summary-item">
                                <img src={item.imageUrl} alt={item.name} />
                                <div className="summary-item-details">
                                    <h4>{item.name}</h4>
                                    <p>Qty: {item.quantity}</p>
                                </div>
                                <span className="summary-item-price">
                                    KSh {(item.price * item.quantity).toFixed(2)}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="summary-totals">
                        <div className="summary-row">
                            <span>Subtotal</span>
                            <span>KSh {getSubtotal().toFixed(2)}</span>
                        </div>

                        <div className="summary-row">
                            <span>Delivery Fee</span>
                            <span>KSh {deliveryFee.toFixed(2)}</span>
                        </div>

                        <div className="summary-row total">
                            <span>Total</span>
                            <span>KSh {total.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Status Modal */}
            {showPaymentModal && (
                <div className="payment-modal-overlay">
                    <div className="payment-modal">
                        <div className="payment-modal-content">
                            <h2>M-Pesa Payment</h2>
                            <div className="payment-spinner"></div>
                            <p className="payment-status">{paymentStatus}</p>
                            {paymentStatus.includes('Check your phone') && (
                                <p className="payment-instructions">
                                    1. Enter your M-Pesa PIN<br />
                                    2. Confirm the payment<br />
                                    3. Wait for confirmation
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Checkout;
